import React, { FunctionComponent, useState } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import Modal from '@empiriecom/module-components/Modal';
import { Dimension } from '@/interfaces';
import { SetProduct } from '../types';
import { Header } from '../Header';
import { Tile } from '../Tiles/Tile';
import { ListElement } from './ListElement';

const messages = defineMessages({
  modalHeadline: {
    defaultMessage: 'Bitte {displayName} wählen',
    id: 'product-selection.modal-headline',
  },
});

interface ModalSelectProps {
  /** The dimension used to display the select and the modal content. */
  dimension: Dimension;
  /** Callback function to set the product in the outside context. The dimensions `sku` property is passed as productId.  */
  setProduct: SetProduct;
  /** DEPRECATED function to test onClose behavior of the modal layer. */
  testOnClose?: () => void;
}

const SelectionElement = styled.div<{ isDisabled: boolean }>`
  align-items: center;
  cursor: ${({ isDisabled }) => (isDisabled ? 'default;' : 'pointer;')};
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  border: 1px solid #c3c3c3;
  ${({ isDisabled }) => isDisabled && 'background-color: #f4f4f4; color: rgba(51,51,51,0.7);'}
`;

const TextElement = styled.div`
  flex-grow: 2;
  padding: 0 0.25rem;
`;

const ModalSelectContainer = styled.div`
  margin-bottom: 0.75rem;
`;

export const ModalSelectLayer: FunctionComponent<
  ModalSelectProps & {
    isModalOpen: boolean;
    setIsModalOpen?: (isOpen: boolean) => void;
  }
> = ({
  dimension: { displayName, values = [] },
  isModalOpen,
  setIsModalOpen = () => null,
  setProduct,
  testOnClose,
}) => {
  const intl = useIntl();
  return (
    <Modal
      headline={intl.formatMessage(messages.modalHeadline, { displayName })}
      isOpen={isModalOpen}
      noContentPadding
      onRequestClose={(): void => {
        if (testOnClose) {
          testOnClose();
        }
        setIsModalOpen(false);
      }}
      testId="product-selection-select-layer"
      testIdClose="product-selection-select-layer-close"
    >
      <>
        {values.map(
          (variant): JSX.Element => {
            const { sku, isSelected } = variant;
            return (
              <div key={sku}>
                <ListElement
                  onClick={() => {
                    if (!isSelected) {
                      setProduct(sku);
                      setIsModalOpen(false);
                    }
                  }}
                  variant={variant}
                />
              </div>
            );
          },
        )}
      </>
    </Modal>
  );
};

export const ModalSelect: FunctionComponent<ModalSelectProps> = ({
  dimension,
  setProduct,
  testOnClose,
}) => {
  const { displayName, values = [] } = dimension;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dimensionIsSelected = values.filter(({ isSelected }) => isSelected).length;
  const isDisabled = dimensionIsSelected && values.length <= 1;
  const ArrowRight = getIcon('ArrowMediumRight');
  const [value] = values
    .filter(({ isSelected }) => isSelected)
    .map(({ displayText, rgb, image }) => ({
      displayText,
      image,
      rgb,
    }));
  return (
    <ModalSelectContainer>
      <Header dimensionName={displayName} />
      <SelectionElement
        data-testid="product-selection-select"
        onClick={() => (values.length > 1 || !isDisabled ? setIsModalOpen(true) : null)}
        isDisabled={isDisabled}
      >
        {dimensionIsSelected ? (
          <>
            {(value.rgb || value.image) && <Tile image={value.image} rgb={value.rgb} isInline />}
            <TextElement>{value.displayText}</TextElement>
          </>
        ) : (
          <FormattedMessage id="modal-select.select-value" defaultMessage="Bitte Variante wählen" />
        )}
        {values.length > 1 && <ArrowRight />}
      </SelectionElement>
      <ModalSelectLayer
        dimension={dimension}
        setProduct={setProduct}
        testOnClose={testOnClose}
        isModalOpen={isModalOpen}
        setIsModalOpen={(isOpen) => setIsModalOpen(isOpen)}
      />
    </ModalSelectContainer>
  );
};

export default ModalSelect;
